import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";

import {
  Chip,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DataGrid } from "@mui/x-data-grid";

import { statusColors, statusLabels } from "@/features/purchase-invoices";
import {
  formatDate,
  formatMoney,
  getShortReference,
} from "@/features/shared";

const SummaryItem = ({ label, value, valueColor }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography variant="h6" color={valueColor} sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Stack>
);

SummaryItem.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  valueColor: PropTypes.string,
};

const sumAllocated = (purchaseDownPayment) =>
  (purchaseDownPayment.purchaseDownPaymentAllocations ?? []).reduce(
    (sum, allocation) => sum + (allocation.amount ?? 0),
    0,
  );

export const DownPaymentSummarySection = ({ purchaseOrder, title }) => {
  const purchaseDownPayments = purchaseOrder.purchaseDownPayments ?? [];

  const rows = purchaseDownPayments.map((pdp) => {
    const allocated = sumAllocated(pdp);
    const returned = pdp.returnedAmount ?? 0;

    return {
      id: pdp.id,
      referenceNumber: pdp.referenceNumber,
      transactionDate: pdp.transactionDate,
      status: pdp.status,
      grandTotal: pdp.grandTotal ?? 0,
      used: allocated + returned,
      remaining: (pdp.grandTotal ?? 0) - allocated - returned,
      allocations: pdp.purchaseDownPaymentAllocations ?? [],
    };
  });

  // Total down payment created against this PO, and how much of it has been
  // used (allocated to invoices) or returned.
  const downPaymentAmount = rows.reduce((sum, row) => sum + row.grandTotal, 0);
  const usedDownPaymentAmount = rows.reduce((sum, row) => sum + row.used, 0);
  const remainingDownPaymentAmount = downPaymentAmount - usedDownPaymentAmount;
  const usagePercentage =
    downPaymentAmount > 0
      ? Math.min(100, (usedDownPaymentAmount / downPaymentAmount) * 100)
      : 0;

  const columns = [
    {
      field: "referenceNumber",
      headerName: "No. Referensi",
      flex: 2,
      renderCell: ({ value }) => (
        <Link
          component={RouterLink}
          to={`/procurement/purchase-down-payments/${value}`}
          variant="body2"
        >
          {value}
        </Link>
      ),
    },
    {
      field: "transactionDate",
      headerName: "Tanggal",
      flex: 1.2,
      valueGetter: (value) => formatDate(value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.2,
      renderCell: ({ value }) =>
        value ? (
          <Chip
            size="small"
            label={statusLabels[value] ?? value}
            color={statusColors[value] ?? "default"}
          />
        ) : (
          "-"
        ),
    },
    {
      field: "grandTotal",
      headerName: "Nilai",
      type: "number",
      flex: 1.2,
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: "used",
      headerName: "Terpakai/Retur",
      type: "number",
      flex: 1.2,
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: "remaining",
      headerName: "Sisa",
      type: "number",
      flex: 1.2,
      renderCell: ({ value }) => (
        <Typography
          variant="body2"
          color={value > 0 ? "warning.main" : "success.main"}
          sx={{ fontWeight: 500 }}
        >
          {formatMoney(value)}
        </Typography>
      ),
    },
    {
      field: "allocations",
      headerName: "Dialokasikan ke",
      flex: 2,
      sortable: false,
      renderCell: ({ value }) => {
        if (!value?.length) return "-";

        return (
          <Stack spacing={0.5} sx={{ py: 1 }}>
            {value.map((allocation) => {
              const reference =
                allocation.targetPurchaseInvoice?.referenceNumber;

              return (
                <Stack
                  key={allocation.id}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "baseline" }}
                >
                  {reference ? (
                    <Link
                      component={RouterLink}
                      to={`/procurement/purchase-invoices/${reference}`}
                      variant="body2"
                    >
                      {getShortReference(reference)}
                    </Link>
                  ) : (
                    <Typography variant="body2">-</Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {formatMoney(allocation.amount)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        );
      },
    },
  ];

  return (
    <Stack spacing={2.5}>
      {title && <Typography variant="subtitle1">{title}</Typography>}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <SummaryItem
              label="Total Pesanan"
              value={formatMoney(purchaseOrder.grandTotal)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SummaryItem
              label="Uang Muka"
              value={formatMoney(downPaymentAmount)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SummaryItem
              label="Terpakai/Retur"
              value={formatMoney(usedDownPaymentAmount)}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <SummaryItem
              label="Sisa Uang Muka"
              value={formatMoney(remainingDownPaymentAmount)}
              valueColor={
                remainingDownPaymentAmount > 0 ? "warning.main" : "success.main"
              }
            />
          </Grid>
        </Grid>

        {downPaymentAmount > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={0.75}>
              <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Penggunaan uang muka
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatMoney(usedDownPaymentAmount)} dari{" "}
                  {formatMoney(downPaymentAmount)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={usagePercentage}
                color={usagePercentage >= 100 ? "success" : "primary"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Stack>
          </>
        )}
      </Paper>

      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Belum ada uang muka untuk pesanan ini.
        </Typography>
      ) : (
        <DataGrid
          getRowHeight={() => "auto"}
          columns={columns}
          rows={rows}
          disableRowSelectionOnClick
          hideFooter
        />
      )}
    </Stack>
  );
};

DownPaymentSummarySection.propTypes = {
  purchaseOrder: PropTypes.object.isRequired,
  // Optional heading — omit inside a tab that already names the section.
  title: PropTypes.string,
};
